import React, { memo } from 'react'
import styled from 'styled-components'

import { type PairModel } from '@root/entities/pair'
import { PairLink } from '@root/entities/pair/ui/common'
import { QuickBuyButton } from '@root/features/trade/swap/ui/desktop'

import { PairExploreItemMetrics } from './metrics/pair-explore-item-metrics'
import { PairExploreItemActions } from './pair-explore-item-actions'
import { PairExploreItemAudit } from './pair-explore-item-audit'
import { PairExploreItemChartTooltip } from './pair-explore-item-chart-tooltip'
import { PairExploreItemHeading } from './pair-explore-item-heading'
import { PairExploreItemIcon } from './pair-explore-item-icon'

import type { PairExploreItemBodyProps } from './pair-explore-item-types'
import type { ClassNameProps } from '@root/shared/react'

type Props = PairExploreItemBodyProps & ClassNameProps

export const PairExploreItemBodyMedium: React.FC<Props> = memo(
  ({ pair, statsPeriod, priceChartInterval, onClick, iconSrcOverride }) => {
    return (
      <Root>
        <PairExploreBackgroundLink
          pair={pair}
          onClick={() => onClick?.({ pair })}
        />
        <Side>
          <PairExploreItemIcon
            pair={pair}
            size={84}
            iconSrcOverride={iconSrcOverride}
          />
        </Side>
        <Body>
          <Heading>
            <UpperGroup>
              <PairExploreItemHeading pair={pair} />
              <PairExploreItemActions pair={pair} />
            </UpperGroup>
            <QuickBuyButtonWrapper>
              <QuickBuyButton pair={pair} withAmount section="exploreFeeds" />
            </QuickBuyButtonWrapper>
          </Heading>
          <PairExploreItemMetrics pair={pair} statsPeriod={statsPeriod} />
          <AuditRow>
            <AuditFade>
              {/* TODO: make audit scrollable, fix TooltipCard rendering issues */}
              <PairExploreItemAudit pair={pair} size={14} />
            </AuditFade>
            <ChartColumn>
              <PairExploreItemChartTooltip
                pair={pair}
                interval={priceChartInterval}
              />
            </ChartColumn>
          </AuditRow>
        </Body>
      </Root>
    )
  },
)

const Root = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
`

const Body = styled.div`
  pointer-events: none;
  position: relative;

  display: grid;
  gap: 3px;
  margin-block: 12px;
  margin-inline-end: 12px;
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 10px;
`

const AuditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const AuditFade = styled.div`
  flex: 1 1 0;
  min-width: 0;

  --mask-inline-start: 0px;
  --mask-inline-end: 16px;
  --horizontal-mask: linear-gradient(
    90deg,
    transparent,
    black var(--mask-inline-start),
    black calc(100% - var(--mask-inline-end)),
    transparent
  );
  --mask: var(--horizontal-mask);

  mask-image: var(--mask);
  mask-size: 100%;
  mask-position: 0 0;
  mask-repeat: no-repeat;
`

const ChartColumn = styled.div`
  flex-shrink: 0;
  position: relative;
`

const UpperGroup = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  align-items: center;
  justify-content: start;
  justify-items: start;
  gap: 2px;
`

const QuickBuyButtonWrapper = styled.div`
  display: grid;
  justify-items: end;
`

const Side = styled.div`
  pointer-events: none;
  position: relative;
  display: grid;
  justify-items: center;
  align-items: center;
  gap: 8px;
  margin: 0 12px;
`

const PairExploreBackgroundLink = styled(PairLink<PairModel>)`
  position: absolute;
  inset: 0;
`
